import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE } from './motion';

const NAV_LINKS = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  { name: 'Services', to: '/services' },
  { name: 'Candidates', to: '/candidates' },
  { name: 'Job Board', to: '/jobs' },
  { name: 'Contact', to: '/contact' },
];

const PublicNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
      className="fixed inset-x-3 top-3 z-50 md:inset-x-6 md:top-4"
    >
      <nav
        className={`mx-auto max-w-6xl rounded-full border transition-all duration-300 ${
          scrolled
            ? 'border-gray-200/80 bg-white/90 shadow-lg shadow-gray-900/5 backdrop-blur-xl'
            : 'border-white/40 bg-white/70 shadow-md shadow-gray-900/5 backdrop-blur-lg'
        }`}
      >
        <div className={`flex items-center justify-between px-4 md:px-6 transition-all duration-300 ${scrolled ? 'py-2' : 'py-2.5 md:py-3'}`}>
          <Link to="/" className="flex shrink-0 items-center" aria-label="Appsytec home">
            <img src="/appsytec-logo.png" alt="Appsytec" className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-9' : 'h-10 md:h-11'}`} />
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => {
              const active = pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    active ? 'text-red-600' : 'text-gray-700 hover:text-red-500'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-full bg-red-50"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                  <span className="relative">{link.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <Link
              to="/login"
              className="group inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-red-500"
            >
              Login
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            className="rounded-full p-2 text-gray-800 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="mx-auto mt-2 max-w-6xl overflow-hidden rounded-3xl border border-gray-200/80 bg-white/95 shadow-xl backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col p-3">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.3, ease: EASE }}
                >
                  <Link
                    to={link.to}
                    className={`block rounded-2xl px-4 py-3 text-base font-semibold transition-colors ${
                      pathname === link.to ? 'bg-red-50 text-red-600' : 'text-gray-800 hover:bg-gray-50 hover:text-red-500'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.04 * NAV_LINKS.length, duration: 0.3, ease: EASE }}
                className="mt-2 border-t border-gray-100 pt-3"
              >
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-1.5 rounded-2xl bg-gray-900 px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-red-500"
                >
                  Login
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default PublicNavbar;
